import React from 'react';
const styles = {"wrapper":"CoverageHeadline-module__wrapper__aiXa8","row":"CoverageHeadline-module__row__BzZ6B","badge":"CoverageHeadline-module__badge__-0WUH","healthy":"CoverageHeadline-module__healthy__-Ytwg","degraded":"CoverageHeadline-module__degraded__Nff1y","paused":"CoverageHeadline-module__paused__Mg9a9","caveat":"CoverageHeadline-module__caveat__z4V4h","pauseNote":"CoverageHeadline-module__pauseNote__PhDu0"};
import { Text } from '../Text.js';
/** The six as §A.3 words them. The code spellings are identifiers; these are what people read. */
const STATE_LABELS = {
    healthy: 'Healthy',
    approaching_limit: 'Approaching limit',
    degraded: 'Degraded',
    paused: 'Paused',
    catching_up: 'Catching up',
    unrecoverable_gap: 'Unrecoverable gap'
};
/**
 * One treatment per state.
 *
 * Before spec 176 this was a two-way branch — `healthy`, and everything else styled as degraded —
 * which would have rendered a paused organization in the warning colour. Replaced rather than
 * extended with a special case beside the old one (Constitution Principle X).
 */
const STATE_CLASSES = {
    healthy: styles.healthy,
    approaching_limit: styles.degraded,
    degraded: styles.degraded,
    paused: styles.paused,
    catching_up: styles.paused,
    unrecoverable_gap: styles.degraded
};
/**
 * The one coverage state, and what it does not tell you.
 *
 * Three honesty rules are visible here rather than left to a reader's assumption.
 *
 * **A healthy state does not prove nothing ever broke.** It is derived from OPEN gaps only, because
 * counting closed ones would leave any organization permanently degraded by its own history, unable
 * to return to healthy after consent was restored.
 *
 * **A healthy state does not prove the audience picture is complete.** Where a provider has no
 * observability declaration, that version's audience cannot be resolved at all — a different and
 * weaker position than a surface we know we cannot watch — so it is said in words instead of being
 * absorbed into a colour.
 *
 * **The summary line does not speak for an organization with no rows.** "N requirements watched,
 * none interrupted" is a statement about requirements, and an organization can be paused with none
 * at all — its events piling up unprocessed while that sentence reads reassuringly. The row count is
 * checked before the claim is made.
 */
export function CoverageHeadline({ state, openGapCount, rowCount, audienceIncomplete, isPaused = false }) {
    return (React.createElement("div", { className: styles.wrapper },
        React.createElement("div", { className: styles.row },
            React.createElement("span", { className: `${styles.badge} ${STATE_CLASSES[state]}` }, STATE_LABELS[state]),
            React.createElement(Text, { muted: true }, summarize(rowCount, openGapCount))),
        isPaused && state !== 'paused' ? (React.createElement("div", { className: styles.pauseNote },
            React.createElement(Text, { muted: true }, "This organization is also paused: work is queued and being caught up. That is a delay rather than lost coverage, which is why it is not the headline here."))) : null,
        audienceIncomplete ? (React.createElement("div", { className: styles.caveat },
            React.createElement(Text, { muted: true }, "The audience picture is incomplete: at least one automation uses a provider with no declared observability, so nothing can be said about who its output can reach."))) : null));
}
function summarize(rowCount, openGapCount) {
    if (rowCount === 0) {
        // Never "none interrupted". With no rows there is nothing to have interrupted, and saying so
        // would read as reassurance to an organization whose backlog is growing.
        return 'No automation requirements are being watched yet.';
    }
    const requirements = `${rowCount} requirement${rowCount === 1 ? '' : 's'}`;
    return openGapCount === 0
        ? `${requirements} watched, none interrupted`
        : `${openGapCount} of ${requirements} interrupted`;
}
//# sourceMappingURL=CoverageHeadline.js.map